/**
 * Student-facing wording for the SSC Rankers League screens. The Mini App's shared catalogue lives in
 * `@exam-prep/i18n` (outside this app); these strings are local so the flow ships on its own.
 * Rules: plain English; never state a fixed day interval for the schedule; no prize wording unless the API sends prizes.
 */

export const GM = {
  title: 'SSC Rankers League',
  /** The label shown with every SSC Rankers League card. */
  extra:
    'An extra exam conducted by the SSCExamPrep team to encourage you. It is not part of the plan you purchased; attending is optional.',
  /** The only schedule sentence we use. */
  schedule:
    "We'll announce each exam date in advance; there's usually one SSC Rankers League every month.",
  noneYet: 'No SSC Rankers League is announced right now.',
  /** A League link opened while the `mocks_enabled` flag is off. */
  mocksOff: 'Mocks are not available yet.',
  open: 'Open SSC Rankers League',
  starts: 'Starts',
  joinUntil: 'You can enter until',
  length: 'Length',
  questions: 'Questions',
  marks: 'Marks',
  resultsAt: 'Results',
  remind: 'Remind me',
  reminded: "Reminder set. We'll message you in the bot before it starts.",
  eligibleYes: 'You can take part.',
  viaPass: 'You can take part with your pass.',
  viaStreak: 'You qualified with your Daily Challenge streak.',
  cancelled: 'This SSC Rankers League was cancelled.',
  missed:
    'Entry is closed for this SSC Rankers League. You can see the results once they are published.',
  paused:
    'The team has paused entry for a moment. Keep this screen open; you can start as soon as it resumes.',
  submittedTitle: 'Your exam is submitted',
  submittedBody: 'Your answers are saved. You cannot re-enter this exam.',
  resultsPending: 'Results are not out yet',
  resultsPendingBody: 'Results are published at the time announced for this SSC Rankers League.',
  back: 'Back',
  retry: 'Try again',
  offline: 'You are offline. Reconnect to continue.',
  loadError: 'We could not load the SSC Rankers League. Check your connection and try again.',
} as const;

export const RULES = {
  readTitle: 'Before you start',
  intro: 'Please read this carefully. The exam begins as soon as you tap Start.',
  points: [
    'You get one attempt. There is no pause.',
    'The exam submits automatically when time runs out. Once you submit, you cannot re-enter.',
    'The server keeps the time. Your answers are saved as you go.',
    'Questions and options are in a different order for each student.',
    'There are no explanations during the exam.',
    'Copying, selecting text, right-click and printing are blocked. Every question carries a faint watermark with your ID.',
    'Leaving this screen is recorded. Please stay in the exam until you submit.',
    'Your phone, battery and internet are your responsibility. We cannot give re-attempts for device or connection problems.',
  ],
  honest:
    'No app can fully stop someone copying, so we also review attempt patterns. A person checks every flagged attempt.',
  fullRules: 'Read the full rules',
  ticksTitle: 'Please confirm',
  agree: 'I agree and start the exam',
  agreeOnly: 'I agree to the rules',
  agreedAhead: 'You have accepted the rules. Come back at the start time and tap Start.',
  start: 'Start the exam',
  starting: 'Starting…',
  saving: 'Saving…',
} as const;

export const ELIGIBILITY = {
  title: 'Can I take part?',
  who: 'Monthly, Till Exam and Annual pass holders can always take part. Free users can qualify with a Daily Challenge streak: the days must be in a row, every question answered, counted since the previous league exam.',
  pass: 'You can take part with your pass.',
  qualified: 'You qualified with your Daily Challenge streak.',
  streakToday:
    "Finish today's Daily Challenge to keep your streak going. Open the bot and tap Daily Challenge.",
  streakOk:
    'Keep going: finish every question of Daily Challenge each day. A missed day resets the count to 0.',
  streakBroken:
    'There are not enough days left before this exam to reach the streak, so you cannot join this SSC Rankers League with a streak. The count starts fresh after the exam, so you can still join the next one, or get a Monthly, Till Exam or Annual pass.',
  needsPass:
    'You are not eligible yet. Build a Daily Challenge streak in the bot (days in a row, every question answered), or get a Monthly, Till Exam or Annual pass.',
  todayCta: 'Open Daily Challenge in the bot',
  plansCta: 'See plans',
} as const;

export const RESULTS = {
  title: 'SSC Rankers League results',
  yourRank: 'Your rank',
  rankOf: (rank: number, cohort: number): string => `#${String(rank)} of ${String(cohort)}`,
  score: 'Score',
  accuracy: 'Accuracy',
  time: 'Time taken',
  leaderboard: 'Leaderboard',
  yourPosition: 'Your position',
  notTaken: 'You did not take part in this SSC Rankers League. Here is how everyone did.',
  disqualified:
    'Your attempt is not ranked. If you think this is a mistake, send /support in the bot.',
  viewAnswers: 'See my answers against the key',
  empty: 'No ranked results to show.',
} as const;
